import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Space, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  DeviationActivityNode,
  DeviationRow,
  DeviationTabKey,
  DeviationTaskNode,
  TAB_META,
  compareGroupLabels,
  explainActual,
  explainTarget,
  fmtINR,
  fmtINRCompact,
  fmtINRSigned,
  fmtQty,
  has,
  machinerySplit,
  num,
} from "./deviationShared";
import { useWbsName } from "../../shared/useWbsCodes";

// ONE register for all three tabs — the tab only changes the Name header and
// how rows group, so a single column grid keeps the ₹ columns lining up when
// the user switches tabs.
//
// FIVE LEVELS on manpower and machinery: norm group -> sub-group -> resource ->
// ACTIVITY -> TASK. The first three answer "which resource is drifting"; the
// last two answer "where", and those are different questions — a rate 8% over
// on one face reads nothing like one 8% over on every face. The drill levels
// come from the row's `breakdown` and are NEVER aggregated back up (they ARE
// the row), which is what `leavesOf` below is guarding.

const INK = "#022c57";
const MUTED = "#8094ae";
const DASH = "#c9d2e3";
const OVER = "#cf1322";
const UNDER = "#237804";

interface TableRow extends DeviationRow {
  key: string;
  isGroup?: boolean;
  /** An activity/task drill row: it is PART of its parent item, not a sibling
   *  of it, so it never feeds an aggregate. */
  drillKind?: "activity" | "task";
  /** Children span more than one UOM, so per-unit columns cannot be rolled up. */
  mixedUom?: boolean;
  /** How many children backed the rolled-up actual / target figures. */
  measuredCount?: number;
  targetedCount?: number;
  /** Machinery cost split (₹ strings, '' = this source contributes nothing). */
  muckingCost?: string;
  toolsCost?: string;
  equipmentCost?: string;
  children?: TableRow[];
}

/** Money that is genuinely absent renders as a dash, not ₹0 — same rule as the
 *  rate columns. A source that contributed nothing to a row has no amount. */
const splitCell = (value: number): string | undefined =>
  value === 0 ? undefined : value.toFixed(2);

/** The machinery split of ONE row, for the Mucking / Tools / Equipment columns. */
const splitOf = (row: DeviationRow) => {
  const split = machinerySplit(row);
  return {
    muckingCost: splitCell(split.muckingCost),
    toolsCost: splitCell(split.toolsCost),
    equipmentCost: splitCell(split.equipmentCost),
  };
};

// Rolls a set of item rows up into the shape of one row, so group headers and
// the grand-total row reuse the SAME cell renderers as the items beneath them.
//
// The rule that keeps this honest: ₹ TOTALS ARE ALWAYS ADDITIVE, PER-UNIT RATES
// ARE NOT. Summing ₹ across a Cum item and an Hours item is fine; averaging
// their rates is not, so every per-unit column is gated on the group sharing one
// UOM and blanks out otherwise (`mixedUom`) rather than inventing a number.
//
// Where a rate IS rolled up it is quantity-weighted over exactly the subset that
// has that rate — so a group whose actual covers 2 of 9 items reports the
// deviation of those 2, not a figure diluted by the 7 that were never measured.
const aggregate = (children: TableRow[]) => {
  const uoms = new Set(
    children.map((child) => child.uomName || "").filter(Boolean)
  );
  const mixedUom = uoms.size > 1;
  const uomName = mixedUom ? "" : Array.from(uoms)[0] || "";

  const sum = (list: TableRow[], field: keyof DeviationRow) =>
    list.reduce((total, child) => total + num(child[field] as string), 0);

  const totalQty = sum(children, "plannedQuantity");
  const plannedCost = sum(children, "plannedCost");

  const targeted = children.filter((child) => has(child.targetRate));
  const measured = children.filter((child) => has(child.actualRate));

  // Quantity-weighted mean of `field` over `subset`; undefined when the subset
  // carries no quantity to weight by.
  const weighted = (subset: TableRow[], field: keyof DeviationRow) => {
    const qty = sum(subset, "plannedQuantity");
    if (qty <= 0) return undefined;
    const acc = subset.reduce(
      (total, child) =>
        total + num(child.plannedQuantity) * num(child[field] as string),
      0
    );
    return acc / qty;
  };

  // impact = (rate delta) × qty, so impact ÷ measured qty gives back the
  // quantity-weighted per-unit delta over precisely the measured rows.
  const perUnit = (subset: TableRow[], field: keyof DeviationRow) => {
    const qty = sum(subset, "plannedQuantity");
    if (qty <= 0) return undefined;
    return sum(subset, field) / qty;
  };

  const rate = (value?: number) =>
    mixedUom || value === undefined ? undefined : value.toFixed(4);

  return {
    uomName,
    mixedUom,
    plannedQuantity: mixedUom ? undefined : totalQty.toFixed(4),
    plannedCost: plannedCost.toFixed(2),
    // Σcost / Σqty — identical to how each item's own boqRate is derived.
    boqRate: rate(totalQty > 0 ? plannedCost / totalQty : undefined),
    targetRate: rate(weighted(targeted, "targetRate")),
    actualRate: rate(weighted(measured, "actualRate")),
    deviation: rate(perUnit(measured, "impactValue")),
    targetDeviation: rate(perUnit(targeted, "targetImpactValue")),
    // ₹ impact stays additive even across mixed UOMs — it is money, not a rate.
    impactValue: measured.length ? sum(measured, "impactValue").toFixed(2) : undefined,
    targetImpactValue: targeted.length
      ? sum(targeted, "targetImpactValue").toFixed(2)
      : undefined,
    measuredCount: measured.length,
    targetedCount: targeted.length,
    // Machinery cost split. Each child's cost lands in exactly one of the three
    // (its source), so these are additive and always foot to plannedCost —
    // this is where a group stops reading as "mucking only" and starts showing
    // the equipment money that is just as much a part of that face's cost.
    ...(() => {
      const totals = children.reduce(
        (acc, child) => {
          const split = machinerySplit(child);
          acc.muckingCost += split.muckingCost;
          acc.toolsCost += split.toolsCost;
          acc.equipmentCost += split.equipmentCost;
          return acc;
        },
        { muckingCost: 0, toolsCost: 0, equipmentCost: 0 }
      );
      return {
        muckingCost: splitCell(totals.muckingCost),
        toolsCost: splitCell(totals.toolsCost),
        equipmentCost: splitCell(totals.equipmentCost),
      };
    })(),
  };
};

// The money/rate fields a drill node shares with a register row. Node field
// names deliberately mirror the row's, so one renderer serves both.
const nodeFields = (node: DeviationActivityNode | DeviationTaskNode) => ({
  plannedQuantity: node.plannedQuantity,
  plannedCost: node.plannedCost,
  boqRate: node.boqRate,
  targetRate: node.targetRate,
  actualRate: node.actualRate,
  deviation: node.deviation,
  deviationPct: node.deviationPct,
  impactValue: node.impactValue,
  targetDeviation: node.targetDeviation,
  targetDeviationPct: node.targetDeviationPct,
  targetImpactValue: node.targetImpactValue,
  actualQuantity: node.actualQuantity,
  lineCount: node.lineCount,
});

// Activity -> task children for one register row. The provenance fields
// (source, head, rateBasis, target/actualSource) ride down from the parent so
// a drilled dash explains itself with the SAME tooltip the item row shows —
// the reason a cell is blank is a property of the grain, not of the face.
const drillChildren = (
  row: DeviationRow,
  parentKey: string,
  activityLabel: (name?: string) => string
): TableRow[] | undefined => {
  const activities = row.breakdown || [];
  if (!activities.length) return undefined;

  const inherited = {
    source: row.source,
    head: row.head,
    uomName: row.uomName,
    rateBasis: row.rateBasis,
    targetSource: row.targetSource,
    actualSource: row.actualSource,
  };

  return activities.map((activity, activityIndex) => {
    const activityKey = `${parentKey}-act${activity.activityId ?? activityIndex}`;
    const tasks = (activity.tasks || []).map((task, taskIndex) => {
      const taskRow: TableRow = {
        ...inherited,
        key: `${activityKey}-task${task.taskId ?? taskIndex}`,
        drillKind: "task",
        itemName: task.taskName || "Untitled task",
        itemCode: task.taskSequenceId || "",
        ...nodeFields(task),
      };
      return { ...taskRow, ...splitOf(taskRow) };
    });
    const activityRow: TableRow = {
      ...inherited,
      key: activityKey,
      drillKind: "activity",
      itemName: activityLabel(activity.activityName) || "Unassigned activity",
      itemCode: "",
      ...nodeFields(activity),
      taskCount: activity.taskCount,
      // undefined, not [] — an empty array still draws an expander, which
      // promises a level that is not there.
      children: tasks.length ? tasks : undefined,
    };
    return { ...activityRow, ...splitOf(activityRow) };
  });
};

const rowKey = (row: DeviationRow, index: number): string =>
  [
    row.source,
    row.materialId,
    row.manpowerNormId,
    row.laborTypeId,
    row.laborCategoryId,
    row.machineryNormId,
    row.toolId,
    row.deploymentNormId,
    row.workUomId,
    index,
  ].join("-");

// A rate that could not be measured renders as an em dash that explains itself
// on hover — never as ₹0.00, which would read as a real zero rate.
const rateCell = (
  value: string | undefined,
  why: string,
  strong = false,
  note?: string
) => {
  if (!has(value)) {
    return (
      <Tooltip title={why}>
        <span style={{ color: DASH }}>—</span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={note ? `${fmtINR(value)} · ${note}` : fmtINR(value)}>
      <span
        style={{
          fontVariantNumeric: "tabular-nums",
          fontWeight: strong ? 700 : undefined,
          color: strong ? INK : undefined,
        }}
      >
        {fmtINRCompact(value)}
      </span>
    </Tooltip>
  );
};

// Deviation convention shared with PlannedVsActualTree and MonthlyExecution:
// quiet dash when unmeasurable, red over, green under, grey at exactly zero.
const deviationCell = (
  value: string | undefined,
  pct: string | undefined,
  why: string,
  strong = false,
  note?: string
) => {
  if (!has(value)) {
    return (
      <Tooltip title={why}>
        <span style={{ color: DASH }}>—</span>
      </Tooltip>
    );
  }
  const dev = num(value);
  const pctText = has(pct)
    ? `${num(pct) > 0 ? "+" : num(pct) < 0 ? "−" : ""}${Math.abs(num(pct)).toFixed(1)}%`
    : "";
  return (
    <Tooltip
      title={`${fmtINRSigned(dev, true)} per unit${pctText ? ` · ${pctText}` : ""}${
        note ? ` · ${note}` : ""
      }`}
    >
      <span
        style={{
          fontVariantNumeric: "tabular-nums",
          fontWeight: strong ? 700 : undefined,
          color: dev === 0 ? MUTED : dev > 0 ? OVER : UNDER,
        }}
      >
        {fmtINRSigned(dev)}
      </span>
    </Tooltip>
  );
};

const impactCell = (value: string | undefined, why: string) => {
  if (!has(value)) {
    return (
      <Tooltip title={why}>
        <span style={{ color: DASH }}>—</span>
      </Tooltip>
    );
  }
  const impact = num(value);
  return (
    <Tooltip title={fmtINRSigned(impact, true)}>
      <b
        style={{
          fontVariantNumeric: "tabular-nums",
          color: impact === 0 ? MUTED : impact > 0 ? OVER : UNDER,
        }}
      >
        {fmtINRSigned(impact)}
      </b>
    </Tooltip>
  );
};

interface Props {
  tab: DeviationTabKey;
  rows: DeviationRow[];
  showNormDelta: boolean;
}

const DeviationSummaryTable: React.FC<Props> = ({ tab, rows, showNormDelta }) => {
  const [search, setSearch] = useState("");
  const meta = TAB_META[tab];
  // Activity names carry their WBS code in the stored name, so drill rows obey
  // the same global "show WBS codes" toggle as every other PMS screen.
  const wbsName = useWbsName();
  // Machinery is the only tab with more than one cost source, and reading its
  // total without seeing that mucking and equipment BOTH make it up is the
  // fastest way to under-count a face.
  const showCostSplit = tab === "MACHINERY";

  // Search spans the drill too: with activities and tasks now under every
  // manpower/machinery row, "which resources touch this face" is a question the
  // register can finally answer, and it would be odd for the box to ignore a
  // name the table can show.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const hit = (value?: string) => (value || "").toLowerCase().includes(term);
    return rows.filter(
      (row) =>
        hit(row.itemName) ||
        hit(row.itemCode) ||
        (row.breakdown || []).some(
          (activity) =>
            hit(activity.activityName) ||
            (activity.tasks || []).some(
              (task) => hit(task.taskName) || hit(task.taskSequenceId)
            )
        )
    );
  }, [rows, search]);

  // Group rows so a tab reads as sections rather than one long list. Manpower
  // and Machinery group down to THREE levels — norm group B3 -> sub-group B3.1
  // -> BOQ code B3.1.1 -> the norms priced under it — because a norm master is
  // not one row per code: machinery_norms holds one row per code × activity, so
  // B3.1.1 is ten separate norms (one per adit, each at its own rate) plus the
  // deployment norm on the same code. Without the code level those eleven rows
  // are identically-labelled siblings with no total for the BOQ line.
  //
  // A LEVEL THAT DOES NOT SEPARATE ANYTHING IS DROPPED, not rendered: it needs
  // at least two distinct labels AND at least one label holding more than one
  // item, or it would just wrap every row in a group of one. That test is why
  // the manpower tab skips the code level on URI-1 (its item codes are unique)
  // without anyone having to special-case it.
  const groups = useMemo(() => {
    const makeGroup = (
      label: string,
      leaves: TableRow[],
      children: TableRow[],
      keyPrefix: string
    ): TableRow => ({
      key: `${keyPrefix}-${label}`,
      isGroup: true,
      source: "",
      itemName: label,
      lineCount: leaves.length,
      // ALWAYS aggregated over the leaf items, never over nested group rows,
      // so a multi-level tab cannot double-count its own subtotals.
      ...aggregate(leaves),
      children,
    });

    const byCost = (a: TableRow, b: TableRow) =>
      num(b.plannedCost) - num(a.plannedCost);

    // Grouping levels BELOW the top one, applied in order.
    const levels = [meta.subGroupLabel, meta.codeLabel].filter(
      Boolean
    ) as Array<(row: DeviationRow) => string | null>;

    // Returns what should sit under a group: either the next level's groups, or
    // the item rows themselves once no remaining level separates them.
    const nest = (
      leaves: TableRow[],
      level: number,
      fallbackLabel: string,
      keyPrefix: string
    ): TableRow[] => {
      if (level >= levels.length) return [...leaves].sort(byCost);

      const byLabel = new Map<string, TableRow[]>();
      let resolved = 0;
      leaves.forEach((row) => {
        const label = levels[level](row);
        if (label) resolved += 1;
        // A row this level cannot label keeps its parent's label rather than
        // being swept into a meaningless "Ungrouped" bucket.
        const key = label ?? fallbackLabel;
        byLabel.set(key, [...(byLabel.get(key) || []), row]);
      });

      const separates =
        resolved > 0 &&
        byLabel.size >= 2 &&
        Array.from(byLabel.values()).some((list) => list.length > 1);
      if (!separates) return nest(leaves, level + 1, fallbackLabel, keyPrefix);

      return Array.from(byLabel.entries())
        .map(([label, subLeaves]) =>
          makeGroup(
            label,
            subLeaves,
            nest(subLeaves, level + 1, label, `${keyPrefix}-${label}`),
            keyPrefix
          )
        )
        .sort(byCost);
    };

    const byTopLabel = new Map<string, TableRow[]>();
    filtered.forEach((row, index) => {
      const label = meta.groupLabel(row);
      const list = byTopLabel.get(label) || [];
      const key = rowKey(row, index);
      list.push({
        ...row,
        key,
        ...splitOf(row),
        children: drillChildren(row, key, wbsName),
      });
      byTopLabel.set(label, list);
    });

    return Array.from(byTopLabel.entries())
      .map(([label, leaves]) =>
        makeGroup(label, leaves, nest(leaves, 0, label, `sub-${label}`), "group")
      )
      .sort((a, b) => {
        const cost = byCost(a, b);
        return cost !== 0
          ? cost
          : compareGroupLabels(a.itemName || "", b.itemName || "");
      });
  }, [filtered, meta, wbsName]);

  // Every REGISTER ITEM under a group, at any grouping depth. The grand total
  // and the expand-all control both need this, and both would be wrong without
  // the isGroup guard: recursing blindly would descend past the items into
  // their activity/task drill and count each item's own cost a second time.
  const leavesOf = (row: TableRow): TableRow[] =>
    row.isGroup && row.children ? row.children.flatMap(leavesOf) : [row];

  const grandTotal = useMemo(
    () => aggregate(groups.flatMap(leavesOf)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups]
  );

  // "Expand all" opens the GROUPING, not the drill — otherwise one click would
  // unfold every task on the project and bury the register it is meant to show.
  const allGroupKeys = useMemo(() => {
    const keys: React.Key[] = [];
    const walk = (row: TableRow) => {
      if (!row.isGroup || !row.children) return;
      keys.push(row.key);
      row.children.forEach(walk);
    };
    groups.forEach(walk);
    return keys;
  }, [groups]);

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  // Collapse on a fresh dataset: the group headers ARE the overview, and
  // "Expand all" is one click. Deps deliberately exclude `search`, so typing in
  // the box does not fight the user's expand choices.
  useEffect(() => {
    setExpandedRowKeys([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, tab]);

  // Why a rolled-up cell is blank: either the group spans several units (so a
  // per-unit figure would be meaningless) or nothing under it was measurable.
  const groupWhy = (row: TableRow, kind: "target" | "actual") =>
    row.mixedUom
      ? "Items in this group use different units, so a per-unit figure cannot be rolled up. The ₹ columns still total."
      : kind === "target"
        ? "No item in this group has a norm-master rate"
        : "No item in this group has an actual rate yet";

  // A rolled-up actual/target is quantity-weighted over ONLY the children that
  // had one. Saying so is what stops a PM reading a partial figure as complete
  // — and explains why the group's Deviation is not simply Actual − BOQ when
  // the coverage is partial (the deviation is measured over that same subset).
  const coverage = (row: TableRow, kind: "target" | "actual") => {
    if (!row.isGroup) return undefined;
    const total = row.lineCount || 0;
    const covered =
      (kind === "target" ? row.targetedCount : row.measuredCount) || 0;
    if (!covered || covered === total) return undefined;
    return `weighted over the ${covered} of ${total} items that have ${
      kind === "target" ? "a norm rate" : "an actual rate"
    }`;
  };

  const columns: ColumnsType<TableRow> = [
    Table.EXPAND_COLUMN,
    {
      title: meta.nameHeader,
      dataIndex: "itemName",
      key: "itemName",
      width: 300,
      render: (value: string, row: TableRow) => {
        if (row.isGroup) {
          return (
            <span>
              <b style={{ color: INK }}>{value}</b>
              <span style={{ fontSize: 11.5, color: MUTED, marginLeft: 6 }}>
                {row.lineCount} item{row.lineCount === 1 ? "" : "s"}
              </span>
            </span>
          );
        }
        // Drill rows are the SAME resource seen at a narrower scope, so they
        // read as a location rather than as another item: a quiet label, the
        // scope named, and no item code competing with the resource's own.
        if (row.drillKind) {
          const isActivity = row.drillKind === "activity";
          return (
            <div>
              <Space size={6}>
                <Tag
                  color={isActivity ? "geekblue" : "default"}
                  style={{ marginInlineEnd: 0, fontSize: 10 }}
                >
                  {isActivity ? "Activity" : "Task"}
                </Tag>
                <span style={{ color: isActivity ? INK : undefined }}>
                  {value}
                </span>
              </Space>
              <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>
                {isActivity
                  ? `${row.taskCount || 0} task${row.taskCount === 1 ? "" : "s"}`
                  : row.itemCode || ""}
              </div>
            </div>
          );
        }
        // A norm master is one row per code × ACTIVITY, so under a code group
        // the siblings are the same BOQ line priced for different faces and
        // would otherwise be indistinguishable ("B3.1.1 — Class II", ten
        // times). The norm's own activity is what tells them apart.
        const face = row.normActivityName ? wbsName(row.normActivityName) : "";
        return (
          <div>
            <div style={{ color: INK }}>{value}</div>
            <Space size={4} style={{ marginTop: 2 }} wrap>
              {row.itemCode ? (
                <span style={{ fontSize: 11.5, color: MUTED }}>{row.itemCode}</span>
              ) : null}
              {face ? (
                <Tooltip title="The activity this norm is priced for — the same BOQ code is normed separately per face">
                  <Tag
                    color="geekblue"
                    style={{ marginInlineEnd: 0, fontSize: 10.5 }}
                  >
                    {face}
                  </Tag>
                </Tooltip>
              ) : null}
              {row.rateBasis === "LIVE" ? (
                <Tooltip title="Equipment cost is recomputed each request from the rate version in force — there is no frozen target or captured actual to deviate against.">
                  <Tag color="blue" style={{ marginInlineEnd: 0, fontSize: 10.5 }}>
                    Live-rated
                  </Tag>
                </Tooltip>
              ) : null}
            </Space>
          </div>
        );
      },
    },
    {
      title: "UOM",
      dataIndex: "uomName",
      key: "uomName",
      width: 90,
      render: (value: string, row: TableRow) => {
        if (row.isGroup && row.mixedUom) {
          return (
            <Tooltip title="Items in this group use different units">
              <span style={{ color: DASH, fontStyle: "italic" }}>mixed</span>
            </Tooltip>
          );
        }
        return <span style={{ color: MUTED }}>{value || "—"}</span>;
      },
    },
    {
      title: "Qty",
      dataIndex: "plannedQuantity",
      key: "plannedQuantity",
      width: 110,
      align: "right",
      render: (value: string, row: TableRow) => {
        if (!has(value)) {
          return (
            <Tooltip title={groupWhy(row, "actual")}>
              <span style={{ color: DASH }}>—</span>
            </Tooltip>
          );
        }
        const text = (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtQty(value)}</span>
        );
        return row.isGroup ? <b style={{ color: INK }}>{text}</b> : text;
      },
    },
    {
      title: "BOQ Rate",
      dataIndex: "boqRate",
      key: "boqRate",
      width: 110,
      align: "right",
      render: (value: string, row: TableRow) =>
        rateCell(
          value,
          row.isGroup
            ? groupWhy(row, "actual")
            : "No rate: this item has no allocated quantity",
          row.isGroup
        ),
    },
    {
      title: "Target Rate",
      dataIndex: "targetRate",
      key: "targetRate",
      width: 115,
      align: "right",
      render: (value: string, row: TableRow) =>
        rateCell(
          value,
          row.isGroup ? groupWhy(row, "target") : explainTarget(row),
          row.isGroup,
          coverage(row, "target")
        ),
    },
    {
      title: "Actual Rate",
      dataIndex: "actualRate",
      key: "actualRate",
      width: 115,
      align: "right",
      render: (value: string, row: TableRow) =>
        rateCell(
          value,
          row.isGroup ? groupWhy(row, "actual") : explainActual(row),
          row.isGroup,
          coverage(row, "actual")
        ),
    },
    {
      title: <Tooltip title="Actual Rate − BOQ Rate, per unit">Deviation</Tooltip>,
      dataIndex: "deviation",
      key: "deviation",
      width: 115,
      align: "right",
      render: (value: string, row: TableRow) =>
        deviationCell(
          value,
          row.deviationPct,
          row.isGroup ? groupWhy(row, "actual") : explainActual(row),
          row.isGroup,
          coverage(row, "actual")
        ),
    },
    {
      title: (
        <Tooltip title="Deviation × allocated quantity — the project's exposure at the actual rate">
          Impact Value
        </Tooltip>
      ),
      dataIndex: "impactValue",
      key: "impactValue",
      width: 125,
      align: "right",
      render: (value: string, row: TableRow) =>
        impactCell(
          value,
          row.isGroup ? "No measurable rows in this group" : explainActual(row)
        ),
    },
  ];

  if (showNormDelta) {
    columns.push(
      {
        title: (
          <Tooltip title="Target Rate − BOQ Rate: how far the current norm master has moved from the rate this was planned at">
            Norm Δ
          </Tooltip>
        ),
        dataIndex: "targetDeviation",
        key: "targetDeviation",
        width: 110,
        align: "right",
        render: (value: string, row: TableRow) =>
          deviationCell(
            value,
            row.targetDeviationPct,
            row.isGroup ? groupWhy(row, "target") : explainTarget(row),
            row.isGroup,
            coverage(row, "target")
          ),
      },
      {
        title: (
          <Tooltip title="Norm Δ × allocated quantity — the re-pricing exposure">
            Norm Impact
          </Tooltip>
        ),
        dataIndex: "targetImpactValue",
        key: "targetImpactValue",
        width: 125,
        align: "right",
        render: (value: string, row: TableRow) =>
          impactCell(
            value,
            row.isGroup ? groupWhy(row, "target") : explainTarget(row)
          ),
      }
    );
  }

  // Machinery cost split. Three sources make up a machinery rupee — the
  // mucking norms, the tools & machinery lines, and the live-costed equipment
  // matrices — and only the sum of all three is the cost of a face. Each row
  // belongs to exactly one source, so the columns stay additive: they always
  // foot to Planned Cost on every group row and on the TOTAL.
  if (showCostSplit) {
    const splitColumn = (
      title: string,
      tip: string,
      dataIndex: "muckingCost" | "toolsCost" | "equipmentCost"
    ): ColumnsType<TableRow>[number] => ({
      title: <Tooltip title={tip}>{title}</Tooltip>,
      dataIndex,
      key: dataIndex,
      width: 120,
      align: "right",
      render: (value: string, row: TableRow) => {
        if (!has(value)) {
          return (
            <Tooltip title={`No ${title.toLowerCase()} cost on this row`}>
              <span style={{ color: DASH }}>—</span>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={fmtINR(value)}>
            <span
              style={{
                fontVariantNumeric: "tabular-nums",
                fontWeight: row.isGroup ? 700 : undefined,
                color: row.isGroup ? INK : undefined,
              }}
            >
              {fmtINRCompact(value)}
            </span>
          </Tooltip>
        );
      },
    });

    columns.push(
      splitColumn(
        "Mucking",
        "Machinery-norm cost — the per-BOQ-code norms applied to tasks",
        "muckingCost"
      ),
      splitColumn(
        "Tools",
        "Tools & machinery lines allocated on the task",
        "toolsCost"
      ),
      splitColumn(
        "Equipment",
        "Deployment-norm cost, recomputed live from the rate version in force",
        "equipmentCost"
      )
    );
  }

  columns.push({
    title: showCostSplit ? (
      <Tooltip title="Mucking + Tools + Equipment — the full machinery cost">
        Planned Cost
      </Tooltip>
    ) : (
      "Planned Cost"
    ),
    dataIndex: "plannedCost",
    key: "plannedCost",
    width: 130,
    align: "right",
    render: (value: string, row: TableRow) => (
      <Tooltip title={fmtINR(value)}>
        {row.isGroup ? (
          <b style={{ fontVariantNumeric: "tabular-nums", color: INK }}>
            {fmtINRCompact(value)}
          </b>
        ) : (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {fmtINRCompact(value)}
          </span>
        )}
      </Tooltip>
    ),
  });

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <Input.Search
          allowClear
          placeholder={`Search ${meta.label.toLowerCase()}…`}
          style={{ width: 260 }}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Space>
          <Button
            type="link"
            onClick={() => setExpandedRowKeys(allGroupKeys)}
          >
            Expand all
          </Button>
          <Button type="link" onClick={() => setExpandedRowKeys([])}>
            Collapse all
          </Button>
        </Space>
      </div>
      <Table<TableRow>
        size="small"
        rowKey="key"
        summary={() => {
          // Built from the flat item rows via the same aggregate(), so the
          // footer can never disagree with the group rows above it.
          const total: TableRow = {
            key: "grand-total",
            isGroup: true,
            source: "",
            itemName: "TOTAL",
            // Leaf rows, not group rows — `filtered` IS the leaf set.
            lineCount: filtered.length,
            ...grandTotal,
          };
          return (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ background: "#eef2f8" }}>
                {columns.map((column, index) => {
                  const key = (column as any).key || `col-${index}`;
                  const dataIndex = (column as any).dataIndex as
                    | keyof TableRow
                    | undefined;
                  const render = (column as any).render;
                  return (
                    <Table.Summary.Cell
                      key={key}
                      index={index}
                      align={(column as any).align}
                    >
                      {key === "itemName" ? (
                        <b style={{ color: INK }}>
                          TOTAL
                          <span
                            style={{
                              fontSize: 11.5,
                              color: MUTED,
                              marginLeft: 6,
                              fontWeight: 400,
                            }}
                          >
                            {filtered.length} items
                          </span>
                        </b>
                      ) : dataIndex && render ? (
                        render(total[dataIndex] as any, total, 0)
                      ) : null}
                    </Table.Summary.Cell>
                  );
                })}
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
        columns={columns}
        dataSource={groups}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
        }}
        onRow={(row) =>
          row.isGroup
            ? { style: { background: "#f5f7fa" } }
            : row.drillKind
              ? // Drill rows sit visually inside their item rather than beside
                // it, so they read as detail, not as more register rows.
                { style: { background: "#fcfdff" } }
              : {}
        }
        pagination={false}
        scroll={{
          x: (showNormDelta ? 1500 : 1250) + (showCostSplit ? 360 : 0),
        }}
      />
    </>
  );
};

export default DeviationSummaryTable;
